<template>
  <div class="cl-panel-stack">
    <article class="card">
      <header class="cl-card-head">
        <div>
          <p class="eyebrow">Players</p>
          <h3>{{ rows.length }} players in this segment</h3>
          <p class="muted-line">{{ basisNote }}</p>
        </div>
        <div class="page-actions">
          <label class="cl-switch">
            <input
              type="checkbox"
              :checked="showNonGroupPlayers"
              @change="$emit('toggleNonGroup', $event.target.checked)"
            >
            <span>Include players outside the group</span>
          </label>
        </div>
      </header>

      <div class="cl-table-scroll">
        <table class="cl-table">
          <thead>
            <tr>
              <th
                v-for="column in columns"
                :key="column.key"
                :class="{ 'is-numeric': column.numeric, 'is-sorted': sortKey === column.key }"
                @click="sortBy(column.key)"
              >
                {{ column.label }}
                <i
                  v-if="sortKey === column.key"
                  class="fa-solid"
                  :class="sortDirection === 'desc' ? 'fa-caret-down' : 'fa-caret-up'"
                ></i>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="player in sortedRows"
              :key="player.guid"
              :class="{ 'is-selected': player.guid === selectedPlayerGuid }"
              @click="$emit('selectPlayer', player.guid)"
            >
              <td>
                <span class="cl-player-cell">
                  <strong class="cl-class-name" :style="{ color: player.color || 'inherit' }">{{ player.name }}</strong>
                  <small>
                    {{ player.spec || '' }} {{ player.className || '' }}
                    <template v-if="player.realm">- {{ player.realm }}</template>
                    <template v-if="!player.isGroupMember"> - outside group</template>
                  </small>
                </span>
              </td>
              <td class="is-numeric">{{ formatNumber(player.damage) }}</td>
              <td class="is-numeric">{{ formatRate(rateOf(player, 'dps')) }}</td>
              <td class="is-numeric">{{ formatPercent(player.damageShare) }}</td>
              <td class="is-numeric">{{ formatNumber(player.healing) }}</td>
              <td class="is-numeric">{{ formatRate(rateOf(player, 'hps')) }}</td>
              <td class="is-numeric">{{ formatPercent(player.overhealRate) }}</td>
              <td class="is-numeric">{{ formatNumber(player.damageTaken) }}</td>
              <td class="is-numeric">{{ formatNumber(player.interrupts) }}</td>
              <td class="is-numeric">{{ formatNumber(player.deaths) }}</td>
              <td class="is-numeric">{{ player.itemLevel || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="!rows.length" class="cl-empty-line">No player activity recorded in this segment.</p>
    </article>

    <template v-if="player">
      <article class="card cl-player-head-card">
        <header class="cl-card-head">
          <div>
            <p class="eyebrow">{{ player.spec || 'Unknown spec' }} {{ player.className }}</p>
            <h3 class="cl-class-name" :style="{ color: player.color || 'inherit' }">{{ player.fullName || player.name }}</h3>
            <p class="muted-line">
              Active for {{ formatDuration(player.activeMs) }} of {{ formatDuration(report.basisMs) }}
              <template v-if="player.petCount"> - {{ player.petCount }} pets or guardians merged into these numbers</template>
            </p>
          </div>
        </header>
        <section class="cl-tile-grid is-compact">
          <div v-for="tile in playerTiles" :key="tile.label" class="cl-tile">
            <span>{{ tile.label }}</span>
            <strong>{{ tile.value }}</strong>
            <small>{{ tile.hint }}</small>
          </div>
        </section>
      </article>

      <div class="cl-two-column">
        <article class="card">
          <header class="cl-card-head">
            <div>
              <p class="eyebrow">Damage</p>
              <h3>By ability</h3>
              <p class="muted-line">Pet abilities are marked with the pet name.</p>
            </div>
          </header>
          <CombatLogBars :rows="details.damageBySpell" :limit="12" :formatter="formatNumber" />
        </article>
        <article class="card">
          <header class="cl-card-head">
            <div>
              <p class="eyebrow">Damage</p>
              <h3>By target</h3>
              <p class="muted-line">Identical NPCs are grouped by creature id.</p>
            </div>
          </header>
          <CombatLogBars :rows="details.damageByTarget" :limit="12" :formatter="formatNumber" />
        </article>
      </div>

      <div v-if="player.healing > 0" class="cl-two-column">
        <article class="card">
          <header class="cl-card-head">
            <div>
              <p class="eyebrow">Healing</p>
              <h3>By ability</h3>
              <p class="muted-line">Effective healing; the overheal column shows what was wasted.</p>
            </div>
          </header>
          <CombatLogBars
            :rows="details.healBySpell"
            :limit="12"
            color="var(--cl-healing)"
            :formatter="formatNumber"
          />
        </article>
        <article class="card">
          <header class="cl-card-head">
            <div>
              <p class="eyebrow">Healing</p>
              <h3>By target</h3>
            </div>
          </header>
          <CombatLogBars
            :rows="details.healByTarget"
            :limit="12"
            color="var(--cl-healing)"
            :formatter="formatNumber"
          />
        </article>
      </div>

      <div class="cl-two-column">
        <article class="card">
          <header class="cl-card-head">
            <div>
              <p class="eyebrow">Defensive</p>
              <h3>Damage taken by ability</h3>
              <p class="muted-line">Absorbed amounts are reported separately and not added to the total.</p>
            </div>
          </header>
          <CombatLogBars
            :rows="details.takenBySpell"
            :limit="12"
            color="var(--cl-taken)"
            :formatter="formatNumber"
          />
        </article>
        <article class="card">
          <header class="cl-card-head">
            <div>
              <p class="eyebrow">Defensive</p>
              <h3>Damage taken by source</h3>
            </div>
          </header>
          <CombatLogBars
            :rows="details.takenBySource"
            :limit="12"
            color="var(--cl-taken)"
            :formatter="formatNumber"
          />
        </article>
      </div>

      <div class="cl-two-column">
        <article class="card">
          <header class="cl-card-head">
            <div>
              <p class="eyebrow">Utility</p>
              <h3>Casts</h3>
              <p class="muted-line">Successful casts counted from SPELL_CAST_SUCCESS.</p>
            </div>
          </header>
          <CombatLogBars
            :rows="details.casts"
            :limit="12"
            color="var(--cl-utility)"
            :formatter="formatNumber"
          />
        </article>
        <article class="card">
          <header class="cl-card-head">
            <div>
              <p class="eyebrow">Utility</p>
              <h3>Buff and debuff uptime</h3>
              <p class="muted-line">Share of the segment duration with the aura active.</p>
            </div>
          </header>
          <ul v-if="details.auras.length" class="cl-uptime-list">
            <li v-for="aura in details.auras.slice(0, 14)" :key="aura.spellId">
              <span class="cl-uptime-name">{{ aura.name }}</span>
              <span class="cl-uptime-track">
                <span class="cl-uptime-fill" :style="{ width: Math.round(aura.uptime * 100) + '%' }"></span>
              </span>
              <span class="cl-uptime-value">
                {{ formatPercent(aura.uptime, 0) }}
                <small>{{ formatDuration(aura.uptimeMs) }} / {{ aura.applications }}x</small>
              </span>
            </li>
          </ul>
          <p v-else class="cl-empty-line">No aura applications recorded for this player.</p>
        </article>
      </div>

      <div class="cl-two-column">
        <article class="card">
          <header class="cl-card-head">
            <div>
              <p class="eyebrow">Utility</p>
              <h3>Interrupted and dispelled</h3>
            </div>
          </header>
          <CombatLogBars
            :rows="details.interrupted"
            :limit="8"
            color="var(--cl-accent-soft)"
            empty-label="No interrupts in this segment."
            :formatter="formatNumber"
          />
          <CombatLogBars
            :rows="details.dispelled"
            :limit="8"
            color="var(--cl-accent-soft)"
            empty-label="No dispels in this segment."
            :formatter="formatNumber"
          />
        </article>
        <article class="card">
          <header class="cl-card-head">
            <div>
              <p class="eyebrow">Combat</p>
              <h3>Avoidance and pets</h3>
            </div>
          </header>
          <CombatLogBars
            :rows="details.missTypes"
            :limit="8"
            color="var(--muted-foreground)"
            empty-label="No misses recorded against this player's attacks."
            :formatter="formatNumber"
          />
          <ul v-if="player.pets && player.pets.length" class="cl-pet-list">
            <li v-for="pet in player.pets" :key="pet.guid">
              <span>{{ pet.name }}</span>
              <strong>{{ formatNumber(pet.damage) }}</strong>
            </li>
            <li v-if="player.petCount > player.pets.length" class="muted-line">
              and {{ player.petCount - player.pets.length }} more short-lived summons
            </li>
          </ul>
        </article>
      </div>
    </template>

    <p v-else-if="rows.length" class="cl-empty-line">Select a player to see the detailed breakdown.</p>
  </div>
</template>

<script>
import CombatLogBars from './CombatLogBars.vue'
import {
  formatNumber,
  formatCompact,
  formatRate,
  formatPercent,
  formatDuration,
  describeBasis,
  basisDurationMs,
} from '../../services/combatLog/format'

const COLUMNS = [
  { key: 'name', label: 'Player', numeric: false },
  { key: 'damage', label: 'Damage', numeric: true },
  { key: 'dps', label: 'DPS', numeric: true },
  { key: 'damageShare', label: 'Share', numeric: true },
  { key: 'healing', label: 'Healing', numeric: true },
  { key: 'hps', label: 'HPS', numeric: true },
  { key: 'overhealRate', label: 'Overheal', numeric: true },
  { key: 'damageTaken', label: 'Taken', numeric: true },
  { key: 'interrupts', label: 'Interrupts', numeric: true },
  { key: 'deaths', label: 'Deaths', numeric: true },
  { key: 'itemLevel', label: 'Item level', numeric: true },
]

const EMPTY_DETAILS = {
  damageBySpell: [],
  healBySpell: [],
  damageByTarget: [],
  healByTarget: [],
  takenBySpell: [],
  takenBySource: [],
  casts: [],
  interrupted: [],
  dispelled: [],
  missTypes: [],
  auras: [],
}

export default {
  name: 'CombatLogPlayers',
  components: { CombatLogBars },
  props: {
    report: { type: Object, required: true },
    basis: { type: String, default: 'segment' },
    selectedPlayerGuid: { type: String, default: '' },
    showNonGroupPlayers: { type: Boolean, default: false },
  },
  emits: ['selectPlayer', 'toggleNonGroup'],
  data() {
    return { columns: COLUMNS, sortKey: 'damage', sortDirection: 'desc' }
  },
  computed: {
    rows() {
      if (!this.showNonGroupPlayers) return this.report.players
      return [...this.report.players, ...this.report.others]
    },
    sortedRows() {
      const direction = this.sortDirection === 'asc' ? 1 : -1
      const key = this.sortKey
      return [...this.rows].sort((left, right) => {
        if (key === 'name') return left.name.localeCompare(right.name) * direction
        if (key === 'dps' || key === 'hps') {
          return (this.rateOf(left, key) - this.rateOf(right, key)) * direction
        }
        return ((left[key] || 0) - (right[key] || 0)) * direction
      })
    },
    player() {
      return this.rows.find((row) => row.guid === this.selectedPlayerGuid) || null
    },
    details() {
      if (!this.player) return EMPTY_DETAILS
      return { ...EMPTY_DETAILS, ...(this.report.details[this.player.guid] || {}) }
    },
    basisNote() {
      const label = describeBasis(this.report, this.basis)
      return `DPS and HPS are ${label} (${formatDuration(basisDurationMs(this.report, this.basis))}). Click a row for the full breakdown.`
    },
    playerTiles() {
      const player = this.player
      if (!player) return []
      const tiles = [
        { label: 'Damage', value: formatCompact(player.damage), hint: `${formatRate(this.rateOf(player, 'dps'))} - ${formatPercent(player.damageShare)} of group` },
        { label: 'Crit rate', value: formatPercent(player.critRate), hint: `${formatNumber(player.damageHits)} damaging hits` },
        { label: 'Overkill', value: formatCompact(player.overkill), hint: 'Damage beyond the target health' },
        { label: 'Damage taken', value: formatCompact(player.damageTaken), hint: `${formatCompact(player.damageTakenAbsorbed)} absorbed` },
        { label: 'Healing received', value: formatCompact(player.healingReceived), hint: `${formatCompact(player.absorbDone)} absorb provided` },
        { label: 'Casts', value: formatNumber(player.casts), hint: `${formatNumber(player.interrupts)} interrupts, ${formatNumber(player.dispels)} dispels` },
        { label: 'Deaths', value: formatNumber(player.deaths), hint: player.killingBlows ? `${formatNumber(player.killingBlows)} killing blows` : '' },
      ]
      if (player.healing > 0) {
        tiles.splice(3, 0, {
          label: 'Healing',
          value: formatCompact(player.healing),
          hint: `${formatRate(this.rateOf(player, 'hps'))} - ${formatPercent(player.overhealRate)} overheal`,
        })
      }
      return tiles
    },
  },
  methods: {
    formatNumber,
    formatRate,
    formatPercent,
    formatDuration,
    rateOf(player, key) {
      if (key === 'dps') return this.basis === 'active' ? player.activeDps : player.dps
      return this.basis === 'active' ? player.activeHps : player.hps
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc'
        return
      }
      this.sortKey = key
      this.sortDirection = key === 'name' ? 'asc' : 'desc'
    },
  },
}
</script>
